import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { Button, Input } from '../../../components';
import { authService } from '../services/authService';

export default function ForgotPasswordForm() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const mutation = useMutation({
    mutationFn: (email: string) => authService.forgotPassword(email),
    onSuccess: () => setSubmitted(true),
    onError: () => setSubmitted(true), // Always show success to avoid leaking info
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email');
      return;
    }

    mutation.mutate(email);
  };

  if (submitted) {
    return (
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-brand/10">
          <EnvelopeIcon className="h-6 w-6 text-brand" />
        </div>
        <h1 className="text-2xl font-bold text-ink font-display">Check your email</h1>
        <p className="mt-2 text-sm text-ink-2">
          If an account exists for <strong>{email}</strong>, we've sent a password reset link.
          Please check your inbox and spam folder.
        </p>
        <Link
          to="/login"
          className="mt-6 inline-block text-sm font-medium text-brand hover:text-brand-ink"
        >
          Back to sign in
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-ink font-display">Forgot your password?</h1>
        <p className="mt-2 text-sm text-ink-2">
          Enter your email address and we'll send you a link to reset your password.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mt-8 space-y-6">
        <Input
          type="email"
          label="Email address"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={error}
          leftIcon={<EnvelopeIcon className="h-5 w-5" />}
          autoComplete="email"
          autoFocus
        />

        <Button type="submit" className="w-full" isLoading={mutation.isPending}>
          Send reset link
        </Button>

        <p className="text-center text-sm text-ink-2">
          Remember your password?{' '}
          <Link to="/login" className="font-medium text-brand hover:text-brand-ink">
            Sign in
          </Link>
        </p>
      </form>
    </div>
  );
}
